import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Send, Loader2, MessageSquare } from 'lucide-react';
import type { ClarifyingQA } from '../../types';

interface ClarificationControlsProps {
  clarifyingQA: ClarifyingQA[];
  onSubmitAnswers: (answers: Record<number, string>) => void;
  isProcessing: boolean;
}

export function ClarificationControls({
  clarifyingQA,
  onSubmitAnswers,
  isProcessing,
}: ClarificationControlsProps) {
  const [answers, setAnswers] = useState<Record<number, string>>({});

  // Questions that still need answers (answer is null)
  const unansweredQuestions = clarifyingQA
    .map((qa, index) => ({ ...qa, index }))
    .filter((qa) => qa.answer === null);

  // Questions that already have answers
  const answeredQuestions = clarifyingQA.filter((qa) => qa.answer !== null);

  const handleAnswerChange = useCallback((index: number, value: string) => {
    setAnswers((prev) => ({ ...prev, [index]: value }));
  }, []);

  const handleSubmit = useCallback(() => {
    // Only submit answers that have content
    const filledAnswers: Record<number, string> = {};
    for (const [key, value] of Object.entries(answers)) {
      const trimmed = value.trim();
      if (trimmed) {
        filledAnswers[Number(key)] = trimmed;
      }
    }
    if (Object.keys(filledAnswers).length === 0) return;
    onSubmitAnswers(filledAnswers);
    setAnswers({});
  }, [answers, onSubmitAnswers]);

  const hasFilledAnswers = Object.values(answers).some((v) => v.trim().length > 0);

  if (clarifyingQA.length === 0) return null;

  return (
    <div className="flex-1 flex flex-col p-6 overflow-y-auto">
      <div className="max-w-lg mx-auto w-full space-y-6 flex-1 flex flex-col">
        {/* Header */}
        <div className="text-center space-y-2">
          <MessageSquare className="h-8 w-8 mx-auto text-primary" />
          <h2 className="text-xl font-semibold">A few questions first</h2>
          <p className="text-sm text-muted-foreground">
            Answer each question below to help shape your build plan.
          </p>
        </div>

        {/* Previously answered questions (read-only) */}
        {answeredQuestions.length > 0 && (
          <div className="space-y-2">
            {answeredQuestions.map((qa, i) => (
              <div key={`answered-${i}`} className="rounded-md bg-muted/30 p-3 space-y-1">
                <p className="text-sm font-medium">{qa.question}</p>
                <p className="text-sm text-muted-foreground">{qa.answer}</p>
              </div>
            ))}
          </div>
        )}

        {/* Unanswered questions with input fields */}
        {unansweredQuestions.length > 0 && (
          <div className="space-y-4 flex-1">
            {unansweredQuestions.map((qa) => (
              <div key={`q-${qa.index}`} className="space-y-1.5">
                <label className="text-sm font-medium" htmlFor={`qa-${qa.index}`}>
                  {qa.question}
                </label>
                <Textarea
                  id={`qa-${qa.index}`}
                  value={answers[qa.index] || ''}
                  onChange={(e) => handleAnswerChange(qa.index, e.target.value)}
                  placeholder="Type your answer..."
                  className="min-h-[60px] resize-none"
                  disabled={isProcessing}
                />
              </div>
            ))}
          </div>
        )}

        {/* Submit button */}
        <div className="flex justify-center pt-2">
          <Button
            onClick={handleSubmit}
            disabled={isProcessing || !hasFilledAnswers}
            className="w-full"
            size="lg"
          >
            {isProcessing ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Send className="mr-2 h-4 w-4" />
            )}
            Submit Answers
          </Button>
        </div>
      </div>
    </div>
  );
}
